import BookingStepper from "../../components/booking-stepper/BookingStepper";
import { Link, useMatch } from "react-router-dom";
import { getBooking } from "../../services/bookings";
import { getForeignPartnerBooking } from "../../services/affiliate";
import { useQuery } from "@tanstack/react-query";
import ReservationDetails from "../../components/reservation-details/ReservationDetails";
import ForeignPartnerAffiliatePricePage from "../../components/foreign-partner-affiliate-price/ForeignPartnerAffiliatePricePage";
import { Button, Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { ROLES } from "../../constants";
import { bookingHasRequiredDocuments } from "../../utils/helpers";

const isSubmittedStatus = (status) =>
  status === "approved" || status === "pending" || status === "declined";

function Booking() {
  const { t } = useTranslation("common");
  const { currentUser, isLoadingUser } = useAuth();
  const match = useMatch("/book/:bookingId");
  const bookingId = match?.params.bookingId;

  const isForeignPartner = currentUser?.type === ROLES.FOREIGN_PARTNER;

  const { isLoading, isError, data: booking } = useQuery(
    ["booking", bookingId],
    () => getBooking(bookingId),
    {
      enabled: !!bookingId,
      select: (data) => data.data,
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status === 401 || status === 404) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    }
  );

  const { data: foreignPartnerBooking, isLoading: isLoadingForeignPartnerBooking } =
    useQuery(
      ["get-foreign-partner-booking", bookingId],
      () => getForeignPartnerBooking(bookingId),
      {
        enabled: !!bookingId && isForeignPartner,
        select: (data) => data.data,
        retry: false,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
      }
    );

  if (isLoading || isLoadingUser || (isForeignPartner && isLoadingForeignPartnerBooking)) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
      </Flex>
    );
  }

  if (isError || !booking) {
    return (
      <Flex justify="center" align="center" minHeight="60vh" px={4}>
        <VStack spacing={4} textAlign="center">
          <Text fontSize="xl" fontWeight="semibold">
            {t("bookingNotFound")}
          </Text>
          <Text color="gray.600">{t("bookingNotFoundDescription")}</Text>
          <Button as={Link} to="/" colorScheme="blue">
            {t("bookingNotFoundGoHome")}
          </Button>
        </VStack>
      </Flex>
    );
  }

  const hasForeignPartnerClientRecord =
    foreignPartnerBooking &&
    !foreignPartnerBooking.error &&
    foreignPartnerBooking.booking?.uuid === bookingId;

  const needsAffiliatePrice =
    isForeignPartner &&
    hasForeignPartnerClientRecord &&
    booking.currentStatus === "draft" &&
    bookingHasRequiredDocuments(booking);

  const isTouristCompleted =
    booking.paypalOrderId &&
    booking.paypalAuthorizationId &&
    isSubmittedStatus(booking.currentStatus);

  const isForeignPartnerCompleted =
    booking.isAffiliateReservation && isSubmittedStatus(booking.currentStatus);

  if (needsAffiliatePrice) {
    return (
      <ForeignPartnerAffiliatePricePage
        bookingId={bookingId}
        bookingSummary={booking}
        foreignPartnerBooking={foreignPartnerBooking}
      />
    );
  }

  if (isTouristCompleted || isForeignPartnerCompleted) {
    return (
      <ReservationDetails
        bookingSummary={booking}
        hidePaymentSummary={booking.isAffiliateReservation}
      />
    );
  }

  return <BookingStepper />;
}

export default Booking;
